import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Input, Modal, Tabs } from '../../../components/ui';
import { Wallet, Calendar, RefreshCw, Plus, ArrowDownCircle, ArrowUpCircle, DollarSign, Store, ChevronDown, ChevronRight, FileText, Clock, Archive, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { useAuth } from '../../../app/auth';
import { fechaLocalYMD } from '../../../utils/date';

// Tipos
interface SaldoLocal {
  local_id: string;
  local_nombre: string;
  grupo_id?: string | null;
  saldo_actual: string;
  saldo_real?: string;
  saldo_post_sorteo?: string;
  updated_at: string | null;
  ultimo_movimiento: {
    tipo: string;
    fecha: string;
    monto: string;
  } | null;
  es_propio?: boolean;
  limite_saldo?: string | null;
  credito?: {
    aplica_limite_saldo: boolean;
    bloqueado_por_saldo: boolean;
    nivel_alerta: string | null;
    disponible: string | null;
    saldo_post_sorteo: string | null;
    saldo_real: string | null;
    limite_saldo: string | null;
    mensaje_bloqueo: string | null;
  } | null;
}

interface SaldoGrupo {
  grupo: string;
  ventas: number;
  premios: number;
  saldo: number;
}

interface PreviewLiquidacion {
  local_id: string;
  local_nombre: string;
  fecha: string;
  total_vendido: string;
  total_premios: string;
  total_premios_totales?: string;
  total_comision: string;
  neto_dia: string;
  saldo_actual: string;
  sorteos: { sorteo: string; vendido: string; premios: string; premios_totales?: string; comision: string }[];
}

interface PreviewCierre {
  local_id: string;
  local_nombre: string;
  fecha_hasta: string;
  saldo_actual: string;
  movimientos_a_eliminar: number;
  movimientos_posteriores: number;
  ya_consolidado: boolean;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido?: string;
    premios?: string;
    comision?: string;
    sorteos?: { sorteo: string; vendido: string; premios: string; comision: string }[];
    movimientos_consolidados?: number;
    fecha_desde?: string;
    fecha_hasta?: string;
  } | null;
  created_by: string;
  created_at: string;
}

interface MovimientosPaginados {
  items: Movimiento[];
  total_count: number;
  pagina: number;
  por_pagina: number;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Me Pagan',
  PAGO: 'Yo Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

// ========================
// TAB 1: SALDOS
// ========================
const SaldosTab: React.FC = () => {
  const { user } = useAuth();
  // BANCA y SUPERVISOR pueden registrar abonos/pagos sobre locales de su banca.
  const puedeOperarSaldos = user?.role === 'BANCA' || user?.role === 'SUPERVISOR';
  const navigate = useNavigate();
  const [saldos, setSaldos] = useState<SaldoLocal[]>([]);
  const [saldosGrupo, setSaldosGrupo] = useState<SaldoGrupo[]>([]);
  const [vista, setVista] = useState<'local' | 'grupo'>('local');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendientes, setPendientes] = useState<{id:string;nombre:string;fecha:string}[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTipo, setModalTipo] = useState<'ABONO' | 'PAGO'>('ABONO');
  const [localSeleccionado, setLocalSeleccionado] = useState<SaldoLocal | null>(null);
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLocal, setHistorialLocal] = useState<SaldoLocal | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const cargarSaldos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dataLocal, dataGrupo, pend] = await Promise.all([
        fetchAPI<SaldoLocal[]>('/saldos'),
        fetchAPI<SaldoGrupo[]>('/banca/saldo/grupos'),
        fetchAPI<{id:string;nombre:string;fecha:string}[]>('/sorteos/pendientes-ganador').catch(() => []),
      ]);
      setSaldos(dataLocal);
      setSaldosGrupo(dataGrupo);
      setPendientes(pend);
    } catch (e: any) {
      setError(e.message || 'Error al cargar saldos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarSaldos();
  }, [cargarSaldos]);

  const abrirModal = (local: SaldoLocal, tipo: 'ABONO' | 'PAGO') => {
    setLocalSeleccionado(local);
    setModalTipo(tipo);
    setMonto('');
    setReferencia('');
    setError('');
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!localSeleccionado || !monto) return;
    setGuardando(true);
    setError('');
    try {
      const endpoint = modalTipo === 'ABONO' ? '/saldos/abono' : '/saldos/pago';
      await fetchAPI<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado.local_id,
          monto: parseFloat(monto),
          referencia: referencia || undefined,
        }),
      });
      setModalOpen(false);
      cargarSaldos();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const abrirHistorial = async (local: SaldoLocal) => {
    setHistorialLocal(local);
    setHistorialOpen(true);
    setLoadingHistorial(true);
    try {
      const data = await fetchAPI<MovimientosPaginados>(`/saldos/movimientos?local_id=${local.local_id}&limit=100`);
      setMovimientos(data.items.filter(m => m.tipo === 'ABONO' || m.tipo === 'PAGO'));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const resumenActual = vista === 'local'
    ? saldos.map((item) => Number(item.saldo_post_sorteo ?? item.saldo_actual))
    : saldosGrupo.map((item) => Number(item.saldo));

  const totalDeben = resumenActual.reduce((acc, valor) => (valor > 0 ? acc + valor : acc), 0);
  const totalDebemos = resumenActual.reduce((acc, valor) => (valor < 0 ? acc + Math.abs(valor) : acc), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saldos</h1>
          <p className="text-gray-500">Consulta por local o agrupado por grupo de locales</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button onClick={() => setVista('local')} className={`px-3 py-1.5 text-sm rounded-md ${vista === 'local' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Todos</button>
            <button onClick={() => setVista('grupo')} className={`px-3 py-1.5 text-sm rounded-md ${vista === 'grupo' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Por grupo</button>
          </div>
          <button onClick={cargarSaldos} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Refrescar">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {error && !modalOpen && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {pendientes.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{pendientes.length} sorteo(s) sin ganador.</strong> Esto causa diferencias entre saldo real y post-sorteo.
            </p>
          </div>
          <button
            onClick={() => navigate('/banca/premios')}
            className="w-full sm:w-auto px-3 py-2 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 whitespace-nowrap text-center"
          >
            Asignar ganadores
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ArrowUpCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Nos Deben</p>
              <p className="text-2xl font-bold text-red-600">{formatMonto(String(totalDeben))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowDownCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Debemos</p>
              <p className="text-2xl font-bold text-green-600">{formatMonto(String(totalDebemos))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Saldo Neto</p>
              <p className={`text-2xl font-bold ${totalDeben - totalDebemos >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatMonto(String(totalDeben - totalDebemos))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : vista === 'local' ? (
          saldos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay saldos registrados. Ejecute una liquidación diaria para comenzar.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Grupo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Ultimo Mov.</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {saldos.map((s) => {
                    const saldoPost = Number(s.saldo_post_sorteo ?? s.saldo_actual);
                    const saldoReal = Number(s.saldo_real ?? s.saldo_actual);
                    const hayDiferencia = Math.abs(saldoReal - saldoPost) >= 1;
                    return (
                      <tr key={s.local_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Store size={16} className="text-gray-400" />
                            <span className="font-medium text-gray-900">{s.local_nombre}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{s.grupo_id || 'Sin grupo'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-lg font-bold ${saldoPost > 0 ? 'text-red-600' : saldoPost < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {formatMonto(s.saldo_post_sorteo ?? s.saldo_actual)}
                          </span>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Saldo post-sorteo</p>
                          {saldoPost > 0 && <p className="text-xs text-red-500">Debe a banca</p>}
                          {saldoPost < 0 && <p className="text-xs text-green-500">Banca debe</p>}
                          {hayDiferencia && (
                            <div className="mt-1.5 pt-1.5 border-t border-dashed border-gray-300">
                              <span className={`text-sm font-semibold ${saldoReal > 0 ? 'text-red-500' : saldoReal < 0 ? 'text-green-500' : 'text-gray-500'}`}>
                                {formatMonto(s.saldo_real ?? s.saldo_actual)}
                              </span>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Saldo real</p>
                            </div>
                          )}
                          {!s.es_propio && s.credito?.aplica_limite_saldo && (() => {
                            const c = s.credito!;
                            const bloqueado = c.bloqueado_por_saldo;
                            const disponible = Number(c.disponible || 0);
                            const limite = Number(c.limite_saldo || 0);
                            const pctRest = limite > 0 ? Math.round((disponible / limite) * 100) : 100;
                            const color = bloqueado ? 'text-red-700 font-bold' : pctRest <= 5 ? 'text-red-600' : pctRest <= 10 ? 'text-red-500' : pctRest <= 20 ? 'text-amber-600' : 'text-gray-500';
                            return (
                              <p className={`text-xs mt-0.5 ${color} ${!bloqueado && pctRest <= 5 ? 'animate-pulse' : ''}`}>
                                {bloqueado ? 'BLOQUEADO' : `Disp: ${formatMonto(String(disponible))}`} / Lím: {formatMonto(String(limite))}
                              </p>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {s.ultimo_movimiento ? (
                            <div className="text-sm">
                              <Badge variant={TIPO_BADGE[s.ultimo_movimiento.tipo] || 'neutral'}>
                                {TIPO_LABELS[s.ultimo_movimiento.tipo] || s.ultimo_movimiento.tipo}
                              </Badge>
                              <p className="text-xs text-gray-400 mt-1">{s.ultimo_movimiento.fecha}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col sm:flex-row gap-1 justify-center">
                            {puedeOperarSaldos && <button onClick={() => abrirModal(s, 'ABONO')} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200">Me Pagan</button>}
                            {puedeOperarSaldos && <button onClick={() => abrirModal(s, 'PAGO')} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200">Yo Pago</button>}
                            <button onClick={() => abrirHistorial(s)} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200">Historial</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          saldosGrupo.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay grupos configurados para esta banca.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Grupo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Ventas</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Premios</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {saldosGrupo.map((grupo) => {
                    const saldo = Number(grupo.saldo);
                    return (
                      <tr key={grupo.grupo} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{grupo.grupo}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{formatMonto(String(grupo.ventas))}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{formatMonto(String(grupo.premios))}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-lg font-bold ${saldo > 0 ? 'text-red-600' : saldo < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {formatMonto(String(grupo.saldo))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>

      {/* Modal Abono/Pago */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTipo === 'ABONO' ? 'Registrar Me Pagan' : 'Registrar Yo Pago'}
      >
        <div className="space-y-4">
          {localSeleccionado && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Local:</p>
              <p className="font-semibold">{localSeleccionado.local_nombre}</p>
              <p className="text-sm text-gray-500 mt-1">Saldo post-sorteo:</p>
              <p className={`font-bold ${Number(localSeleccionado.saldo_post_sorteo ?? localSeleccionado.saldo_actual) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatMonto(localSeleccionado.saldo_post_sorteo ?? localSeleccionado.saldo_actual)}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {modalTipo === 'ABONO' ? 'Monto de Me Pagan' : 'Monto de Yo Pago'}
            </label>
            <input
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0"
              min={0}
              step={1}
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia (opcional)</label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej: Deposito bancario, Efectivo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              variant={modalTipo === 'ABONO' ? 'success' : 'warning'}
              onClick={guardar}
              disabled={guardando || !monto || parseFloat(monto) <= 0}
            >
              {guardando ? 'Guardando...' : modalTipo === 'ABONO' ? 'Registrar Me Pagan' : 'Registrar Yo Pago'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Historial */}
      <Modal
        isOpen={historialOpen}
        onClose={() => setHistorialOpen(false)}
        title={`Historial - ${historialLocal?.local_nombre || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {loadingHistorial ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos</div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-2">Fecha</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2 text-right">Monto</th>
                    <th className="py-2 pr-2 text-right">Saldo</th>
                    <th className="py-2">Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 text-xs">{m.fecha}</td>
                      <td className="py-2 pr-2">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`py-2 pr-2 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className={`py-2 pr-2 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                        {formatMonto(m.saldo_resultante)}
                      </td>
                      <td className="py-2 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};


// ========================
// TAB 2: LIQUIDACION DIARIA
// ========================
const LiquidacionTab: React.FC = () => {
  const [fechaLiq, setFechaLiq] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewLiquidacion | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Cargar locales
  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewLiquidacion>(
        `/saldos/preview-liquidacion?local_id=${localSeleccionado}&fecha=${fechaLiq}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarLiquidacion = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/liquidacion', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha: fechaLiq,
        }),
      });
      setMensaje(`Resumen del dia: Neto ${formatMonto(result.neto_dia)}. Saldo actual: ${formatMonto(result.saldo_nuevo)}`);
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar liquidación');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Liquidación Diaria</h1>
        <p className="text-gray-500">Resumen informativo: ventas - comisiones - premios del dia</p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <Input
              type="date"
              value={fechaLiq}
              onChange={(e) => setFechaLiq(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Calcular'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            {/* Header info */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Fecha: {preview.fecha}</p>
              </div>
            </div>

            {/* Desglose */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Ventas</p>
                <p className="text-lg font-bold text-blue-700">{formatMonto(preview.total_vendido)}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-600 font-medium">Comisiones</p>
                <p className="text-lg font-bold text-yellow-700">-{formatMonto(preview.total_comision)}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium">Premios cambiados</p>
                <p className="text-lg font-bold text-red-700">-{formatMonto(preview.total_premios)}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg" title="Premios generados por el sorteo (incluye no cobrados). Solo informativo: no afecta el saldo.">
                <p className="text-xs text-amber-700 font-medium">Premios totales</p>
                <p className="text-lg font-bold text-amber-800">{formatMonto(preview.total_premios_totales ?? preview.total_premios)}</p>
              </div>
              <div className={`p-3 rounded-lg ${Number(preview.neto_dia) >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                <p className={`text-xs font-medium ${Number(preview.neto_dia) >= 0 ? 'text-green-600' : 'text-orange-600'}`}>Neto del Dia</p>
                <p className={`text-lg font-bold ${Number(preview.neto_dia) >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                  {formatMonto(preview.neto_dia)}
                </p>
              </div>
            </div>

            {/* Saldo actual */}
            <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Saldo actual del local</p>
                  <p className={`text-xl font-bold ${Number(preview.saldo_actual) > 0 ? 'text-red-600' : Number(preview.saldo_actual) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                    {formatMonto(preview.saldo_actual)}
                  </p>
                </div>
              </div>
            </div>

            {/* Detalle sorteos */}
            {preview.sorteos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Detalle por sorteo:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-2">Sorteo</th>
                        <th className="py-2 pr-2 text-right">Vendido</th>
                        <th className="py-2 pr-2 text-right">Premios cambiados</th>
                        <th className="py-2 pr-2 text-right">Premios totales</th>
                        <th className="py-2 text-right">Comision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sorteos.map((s, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-2 font-medium">{s.sorteo}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.vendido)}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.premios)}</td>
                          <td className="py-2 pr-2 text-right text-amber-700">{formatMonto(s.premios_totales ?? s.premios)}</td>
                          <td className="py-2 text-right">{formatMonto(s.comision)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Boton ver resumen confirmado */}
            <div className="pt-4 border-t">
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={ejecutarLiquidacion}
                  disabled={ejecutando}
                  leftIcon={<DollarSign size={18} />}
                >
                  {ejecutando ? 'Consultando...' : 'Confirmar Resumen'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 3: CIERRE CONSOLIDADO
// ========================
const CierreTab: React.FC = () => {
  const [fechaHasta, setFechaHasta] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewCierre | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewCierre>(
        `/saldos/preview-cierre?local_id=${localSeleccionado}&fecha_hasta=${fechaHasta}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCierre = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/cierre', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha_hasta: fechaHasta,
        }),
      });
      setMensaje(
        `Cierre ejecutado. ${result.movimientos_eliminados} movimientos consolidados. ` +
        `Saldo: ${formatMonto(result.saldo_actual)} (sin cambios)`
      );
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar cierre');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cierre Consolidado</h1>
        <p className="text-gray-500">Limpieza administrativa del historial. El saldo no cambia.</p>
      </div>

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Operación técnica:</strong> Consolida todos los movimientos anteriores a la fecha seleccionada
          en un solo registro resumen. Los movimientos antiguos se eliminan pero el saldo permanece exactamente igual.
          Útil para limpiar historial acumulado.
        </p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Consolidar hasta fecha</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Preview'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Consolidar hasta: {preview.fecha_hasta}</p>
              </div>
              {preview.ya_consolidado && (
                <Badge variant="warning">Ya consolidado</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-600 font-medium">Movimientos a eliminar</p>
                <p className="text-2xl font-bold text-orange-700">{preview.movimientos_a_eliminar}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium">Movimientos que quedan</p>
                <p className="text-2xl font-bold text-blue-700">{preview.movimientos_posteriores}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                <p className="text-xs text-gray-600 font-medium">Saldo (no cambia)</p>
                <p className={`text-2xl font-bold ${Number(preview.saldo_actual) > 0 ? 'text-red-600' : Number(preview.saldo_actual) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                  {formatMonto(preview.saldo_actual)}
                </p>
              </div>
            </div>

            {!preview.ya_consolidado && preview.movimientos_a_eliminar > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="danger"
                  onClick={ejecutarCierre}
                  disabled={ejecutando}
                  leftIcon={<Archive size={18} />}
                >
                  {ejecutando ? 'Consolidando...' : 'Ejecutar Cierre'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 4: HISTORIAL
// ========================
const HistorialTab: React.FC = () => {
  const [localId, setLocalId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pagina, setPagina] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const porPagina = 50;

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarMovimientos = async (pag: number = pagina) => {
    setLoading(true);
    setError('');
    try {
      let url = `/saldos/movimientos?pagina=${pag}&por_pagina=${porPagina}`;
      if (localId) url += `&local_id=${localId}`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      const data = await fetchAPI<MovimientosPaginados>(url);
      setMovimientos(data.items);
      setTotalCount(data.total_count);
      setPagina(data.pagina);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos(1);
  }, []);

  const buscar = () => {
    setPagina(1);
    cargarMovimientos(1);
  };

  const irPagina = (pag: number) => {
    cargarMovimientos(pag);
  };

  const totalPaginas = Math.max(1, Math.ceil(totalCount / porPagina));

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Historial de Movimientos</h1>
        <p className="text-gray-500">Todos los movimientos de saldo</p>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
            >
              <option value="">Todos los locales</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={buscar} disabled={loading}>
            {loading ? 'Cargando...' : 'Buscar'}
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Tabla */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : movimientos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin movimientos</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-8"></th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movimientos.map(m => {
                  const isExpanded = expandedRows.has(m.id);
                  const hasDetail = m.detalle && (m.detalle.vendido || m.detalle.movimientos_consolidados);
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => hasDetail && toggleRow(m.id)}>
                        <td className="px-4 py-3">
                          {hasDetail && (
                            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                          )}
                        </td>
                        <td className="px-4 py-3">{m.fecha}</td>
                        <td className="px-4 py-3 font-medium">{m.local_nombre}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                            {TIPO_LABELS[m.tipo] || m.tipo}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {formatMonto(m.saldo_resultante)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                      </tr>
                      {isExpanded && m.detalle && (
                        <tr>
                          <td colSpan={7} className="px-8 py-3 bg-gray-50">
                            {/* Liquidación detail */}
                            {m.detalle.vendido && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
                                  <div>
                                    <p className="text-xs text-gray-500">Ventas</p>
                                    <p className="font-medium">{formatMonto(m.detalle.vendido)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Premios</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.premios || '0')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Comisiones</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.comision || '0')}</p>
                                  </div>
                                </div>
                                {m.detalle.sorteos && m.detalle.sorteos.length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead>
                                      <tr className="text-gray-500 border-b">
                                        <th className="text-left py-1">Sorteo</th>
                                        <th className="text-right py-1">Vendido</th>
                                        <th className="text-right py-1">Premios</th>
                                        <th className="text-right py-1">Comision</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.detalle.sorteos.map((s, i) => (
                                        <tr key={i}>
                                          <td className="py-1">{s.sorteo}</td>
                                          <td className="text-right py-1">{formatMonto(s.vendido)}</td>
                                          <td className="text-right py-1">{formatMonto(s.premios)}</td>
                                          <td className="text-right py-1">{formatMonto(s.comision)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </>
                            )}
                            {/* Cierre consolidado detail */}
                            {m.detalle.movimientos_consolidados && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">Movimientos consolidados</p>
                                  <p className="font-medium">{m.detalle.movimientos_consolidados}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Rango</p>
                                  <p className="font-medium">{m.detalle.fecha_desde} a {m.detalle.fecha_hasta}</p>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginación */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-gray-500">
            {totalCount} movimiento{totalCount !== 1 ? 's' : ''} — Página {pagina} de {totalPaginas}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1 || loading}
              onClick={() => irPagina(pagina - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPaginas || loading}
              onClick={() => irPagina(pagina + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ========================
// TAB: BANQUEROS (BANCA ↔ BANQUERO)
// ========================
interface SaldoBanqueroRow {
  user_id: string;
  username: string;
  nombre: string;
  saldo_actual: string;
  // Desglose económico (misma fórmula que el banquero ve en su pantalla)
  total_vendido?: string;
  total_comision?: string;
  total_premios?: string;
  abonos_banquero?: string;
  pagos_banquero?: string;
}

interface MovBanqueroRow {
  id: string;
  fecha: string;
  tipo: string;
  concepto: string;
  monto: string;
  saldo_resultante: string;
  created_by: string | null;
}

const BanquerosTab: React.FC = () => {
  const [banqueros, setBanqueros] = useState<SaldoBanqueroRow[]>([]);
  const [seleccionado, setSeleccionado] = useState<SaldoBanqueroRow | null>(null);
  const [movs, setMovs] = useState<MovBanqueroRow[]>([]);
  const [saldoSel, setSaldoSel] = useState<string>('0');
  const [limiteVisual, setLimiteVisual] = useState<number>(100);
  const [fechaFiltro, setFechaFiltro] = useState<string>(fechaLocalYMD());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPago, setShowPago] = useState(false);
  const [showAbono, setShowAbono] = useState(false);
  const [opMonto, setOpMonto] = useState('');
  const [opRef, setOpRef] = useState('');
  const [opEnviando, setOpEnviando] = useState(false);
  const [opMsg, setOpMsg] = useState('');

  const cargarBanqueros = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAPI<SaldoBanqueroRow[]>('/saldos/banqueros');
      setBanqueros(data);
    } catch (e: any) {
      setError(e.message || 'Error al cargar banqueros');
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarMovs = useCallback(async (b: SaldoBanqueroRow, fecha?: string) => {
    try {
      const url = fecha
        ? `/saldos/banqueros/${b.user_id}/movimientos?fecha=${fecha}`
        : `/saldos/banqueros/${b.user_id}/movimientos`;
      const data = await fetchAPI<{
        saldo_actual: string;
        total_vendido?: string;
        total_comision?: string;
        total_premios?: string;
        abonos_banquero?: string;
        pagos_banquero?: string;
        breakdown_dia?: {
          total_vendido: string;
          total_comision: string;
          total_premios: string;
          abonos_banquero: string;
          pagos_banquero: string;
        } | null;
        fecha_filtro?: string | null;
        limite_visual_movimientos: number;
        items: MovBanqueroRow[];
      }>(url);
      setMovs(data.items);
      // saldo_actual SIEMPRE histórico — no debe cambiar con el filtro de fecha
      setSaldoSel(data.saldo_actual);
      setLimiteVisual(data.limite_visual_movimientos);
      // Desglose: usar breakdown_dia si hay fecha, sino los campos históricos
      const breakdown = data.breakdown_dia ?? {
        total_vendido: data.total_vendido,
        total_comision: data.total_comision,
        total_premios: data.total_premios,
        abonos_banquero: data.abonos_banquero,
        pagos_banquero: data.pagos_banquero,
      };
      setSeleccionado(prev => prev ? {
        ...prev,
        saldo_actual: data.saldo_actual,
        ...breakdown,
      } : prev);
    } catch (e: any) {
      setError(e.message || 'Error al cargar movimientos');
    }
  }, []);

  useEffect(() => { cargarBanqueros(); }, [cargarBanqueros]);

  // Solo dependemos de user_id y fechaFiltro: cargarMovs hace setSeleccionado
  // (cambia la referencia del objeto), por lo que depender de `seleccionado`
  // completo provocaría un loop de recargas.
  const seleccionadoUserId = seleccionado?.user_id;
  useEffect(() => {
    if (!seleccionadoUserId) return;
    cargarMovs({ user_id: seleccionadoUserId } as SaldoBanqueroRow, fechaFiltro);
  }, [seleccionadoUserId, fechaFiltro, cargarMovs]);

  const fmt = (s: string) => Math.trunc(parseFloat(s) || 0).toLocaleString('es-CR');

  const ejecutarOperacion = async (tipo: 'pago' | 'abono') => {
    if (!seleccionado) return;
    setOpMsg('');
    const monto = parseFloat(opMonto);
    if (!Number.isFinite(monto) || monto <= 0) {
      setOpMsg('Monto inválido');
      return;
    }
    setOpEnviando(true);
    try {
      await fetchAPI(`/saldos/banqueros/${seleccionado.user_id}/${tipo}`, {
        method: 'POST',
        body: JSON.stringify({ monto, referencia: opRef }),
      });
      setOpMonto('');
      setOpRef('');
      setShowPago(false);
      setShowAbono(false);
      await Promise.all([cargarBanqueros(), cargarMovs(seleccionado, fechaFiltro)]);
    } catch (e: any) {
      setOpMsg(e.message || 'Error al registrar');
    } finally {
      setOpEnviando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Saldos de Banqueros</h2>
        <p className="text-sm text-gray-500">
          Libro contable BANCA ↔ BANQUERO. Independiente de los saldos de locales.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lista */}
        <Card className="lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Banqueros</h3>
            <Button variant="ghost" onClick={cargarBanqueros} disabled={loading}>
              <RefreshCw size={16} />
            </Button>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-4">Cargando...</p>
          ) : banqueros.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin banqueros registrados</p>
          ) : (
            <div className="divide-y">
              {banqueros.map((b) => {
                const saldo = parseFloat(b.saldo_actual);
                const activo = seleccionado?.user_id === b.user_id;
                return (
                  <button
                    key={b.user_id}
                    onClick={() => setSeleccionado(b)}
                    className={`w-full text-left py-2 px-2 rounded transition ${
                      activo ? 'bg-blue-50 ring-1 ring-blue-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{b.nombre}</p>
                        <p className="text-xs text-gray-500">{b.username}</p>
                      </div>
                      <span className={`font-mono font-bold text-sm ${
                        saldo > 0 ? 'text-red-700' : saldo < 0 ? 'text-green-700' : 'text-gray-600'
                      }`}>
                        {fmt(b.saldo_actual)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Detalle */}
        <Card className="lg:col-span-2">
          {!seleccionado ? (
            <p className="text-sm text-gray-500 text-center py-10">
              Seleccione un banquero para ver su detalle
            </p>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{seleccionado.nombre}</h3>
                  <p className="text-xs text-gray-500">{seleccionado.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo actual</p>
                  <p className={`text-2xl font-bold ${
                    parseFloat(saldoSel) > 0 ? 'text-red-700'
                      : parseFloat(saldoSel) < 0 ? 'text-green-700' : 'text-gray-700'
                  }`}>
                    {fmt(saldoSel)}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {parseFloat(saldoSel) > 0 ? 'Banquero le debe a la banca'
                      : parseFloat(saldoSel) < 0 ? 'Banca le debe al banquero'
                      : 'Sin saldo pendiente'}
                  </p>
                </div>
              </div>

              {/* Selector de fecha para desglose diario */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Calendar size={15} className="text-gray-400" />
                <label className="text-xs text-gray-500">Desglose del día:</label>
                <input
                  type="date"
                  value={fechaFiltro}
                  onChange={(e) => setFechaFiltro(e.target.value)}
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <span className="text-[10px] text-gray-400">(saldo acumulado no cambia)</span>
              </div>

              {/* Desglose económico — misma fórmula que ve el banquero */}
              {seleccionado.total_vendido !== undefined && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 text-center">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Ventas</p>
                    <p className="text-sm font-bold text-blue-700">₡{fmt(seleccionado.total_vendido ?? '0')}</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Premios</p>
                    <p className="text-sm font-bold text-red-700">₡{fmt(seleccionado.total_premios ?? '0')}</p>
                  </div>
                  <div className="p-2 bg-yellow-50 rounded-lg">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Comisión</p>
                    <p className="text-sm font-bold text-yellow-700">₡{fmt(seleccionado.total_comision ?? '0')}</p>
                  </div>
                  <div className="p-2 bg-green-50 rounded-lg">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Él pagó</p>
                    <p className="text-sm font-bold text-green-700">₡{fmt(seleccionado.abonos_banquero ?? '0')}</p>
                  </div>
                  <div className="p-2 bg-indigo-50 rounded-lg">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Le pagamos</p>
                    <p className="text-sm font-bold text-indigo-700">₡{fmt(seleccionado.pagos_banquero ?? '0')}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mb-4">
                <Button variant="secondary" onClick={() => { setShowPago(true); setOpMsg(''); }}>
                  <ArrowUpCircle size={16} className="mr-1" /> Paga el banquero
                </Button>
                <Button onClick={() => { setShowAbono(true); setOpMsg(''); }}>
                  <ArrowDownCircle size={16} className="mr-1" /> Registrar abono
                </Button>
              </div>

              <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                Histórico
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (últimos {limiteVisual} — el saldo se calcula sobre el total)
                </span>
              </h4>
              {movs.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Sin movimientos</p>
              ) : (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b bg-gray-50 text-gray-600 text-xs uppercase">
                        <th className="px-2 py-2 text-left">Fecha</th>
                        <th className="px-2 py-2 text-center">Tipo</th>
                        <th className="px-2 py-2 text-left">Concepto</th>
                        <th className="px-2 py-2 text-right">Monto</th>
                        <th className="px-2 py-2 text-right">Saldo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {movs.map((m) => {
                        const monto = parseFloat(m.monto);
                        const saldo = parseFloat(m.saldo_resultante);
                        return (
                          <tr key={m.id}>
                            <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">
                              {m.fecha.slice(0, 16).replace('T', ' ')}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${
                                m.tipo === 'ABONO_BANQUERO'
                                  ? 'text-green-700 bg-green-50 border-green-200'
                                  : 'text-orange-700 bg-orange-50 border-orange-200'
                              }`}>
                                {m.tipo === 'ABONO_BANQUERO' ? 'ABONO' : 'PAGO'}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-500 max-w-[260px] truncate" title={m.concepto}>
                              {m.concepto}
                            </td>
                            <td className={`px-2 py-2 text-right font-mono ${monto >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {monto >= 0 ? '+' : ''}{Math.trunc(monto).toLocaleString('es-CR')}
                            </td>
                            <td className={`px-2 py-2 text-right font-mono font-bold ${
                              saldo > 0 ? 'text-red-600' : saldo < 0 ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {Math.trunc(saldo).toLocaleString('es-CR')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Modal pago/abono al banquero — overlay inline para garantizar visibilidad */}
      {(showPago || showAbono) && seleccionado && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => { setShowPago(false); setShowAbono(false); }}
        >
          <div
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-gray-900">
              {showAbono ? 'Registrar abono del banquero' : 'Registrar pago al banquero'}
            </h3>
            <p className="text-sm text-gray-600">
              {showAbono
                ? `${seleccionado.nombre} le entregó dinero a la banca.`
                : `La banca le entrega dinero a ${seleccionado.nombre}.`}
            </p>
            <input
              type="number"
              min="0.01"
              step="any"
              placeholder="Monto"
              value={opMonto}
              onChange={(e) => setOpMonto(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            />
            <input
              type="text"
              placeholder="Referencia (opcional)"
              value={opRef}
              onChange={(e) => setOpRef(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            />
            {opMsg && <p className="text-red-600 text-sm">{opMsg}</p>}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowPago(false); setShowAbono(false); }}
                className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => ejecutarOperacion(showAbono ? 'abono' : 'pago')}
                disabled={opEnviando || !opMonto}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {opEnviando ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ========================
// COMPONENTE PRINCIPAL CON TABS
// ========================
export const SaldosPage: React.FC = () => {
  const { user } = useAuth();
  const esBanca = user?.role === 'BANCA';

  const items = [
    {
      key: 'saldos',
      label: 'Saldos',
      icon: <Wallet size={16} />,
      content: <SaldosTab />,
    },
    ...(esBanca ? [
      {
        key: 'liquidacion',
        label: 'Liquidación',
        icon: <DollarSign size={16} />,
        content: <LiquidacionTab />,
      },
      {
        key: 'cierre',
        label: 'Cierre',
        icon: <Archive size={16} />,
        content: <CierreTab />,
      },
      {
        key: 'banqueros',
        label: 'Banqueros',
        icon: <Wallet size={16} />,
        content: <BanquerosTab />,
      },
    ] : []),
    {
      key: 'historial',
      label: 'Historial',
      icon: <FileText size={16} />,
      content: <HistorialTab />,
    },
  ];

  return <Tabs items={items} />;
};

export default SaldosPage;
